import { type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useStadiumOperatorAccess } from '../../hooks/useStadiumOperatorAccess';
import { ScanStationLoading } from '../../pages/admin/ScanStation/components/ScanStationLoading';

interface Props {
  children: ReactNode;
}

/**
 * Route guard for the Scan Station page. Reads `eventId` from URL,
 * defers to the backend access probe. Renders the shared dark-themed
 * ScanStationLoading screen while checking (so the loading visuals stay
 * consistent across the route's three stages: lazy chunk load, access
 * probe, event/sections fetch).
 *
 * Redirects to /login / /binding / /access-denied as needed.
 */
export function StadiumOperatorRoute({ children }: Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const { isLoading: authLoading, isAuthenticated, isBound } = useAuth();
  const location = useLocation();
  const access = useStadiumOperatorAccess(eventId);

  if (authLoading || access.isLoading) return <ScanStationLoading />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  if (!isBound) return <Navigate to="/binding" state={{ from: location }} replace />;
  if (!access.data) return <Navigate to="/access-denied" replace />;
  return <>{children}</>;
}
